import React, { useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { ContextStore } from '../store/contextStore';

// CP-5545: whether a delete control is usable for this user. `perm` is 'scope.action' (e.g. 'activity.delete'),
// read with hasPerm on every render so a permission change shows without a reload (CP-8056); `allowed` carries a
// check hasPerm can't express (per-licensee, per-row). A user without the permission sees the control disabled,
// not hidden — hide a delete only where the action doesn't apply at all.
export const useDeletePermitted = (perm, allowed = true) => {
    const ctx = useContext(ContextStore);
    if (!allowed) {
        return false;
    }
    if (!perm) {
        return true;
    }
    const [scope, action = 'delete'] = perm.split('.');
    return Boolean(ctx?.hasPerm?.(scope, action));
};

// CP-5545: the dashboard's one icon-only delete (trash = DeleteOutlined, never DeleteOutline). Red when enabled —
// MUI `error`, the same red as FormActionButtons' DELETE — and light grey when disabled. The colour sits on the
// IconButton and the icon stays bare so it inherits: a disabled IconButton greys out on its own, and pinning a
// colour on the icon would break that. A disabled button can't fire a Tooltip's events, hence the <span>;
// describeChild keeps the tooltip from also naming the <span>, so the button alone carries the accessible name.
// The <span> sits at vertical-align: middle like a bare IconButton (ButtonBase sets it), or beside an edit
// icon in running content the trash rides higher on the text baseline (Rea, CP-5545 QA 10/16).
// Pass tooltipProps={{ PopperProps: { style: { zIndex } } }} inside dialogs/drawers that sit above the tooltip layer.
// `overImage` puts a white backing behind the icon so the red reads on top of a photo.
const OVER_IMAGE_SX = { bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' }, '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.6)' } };

// Typed loosely for the .tsx callers: TS would otherwise infer the props of this JS forwardRef as {}.
/** @type {React.ForwardRefExoticComponent<Record<string, any> & React.RefAttributes<HTMLButtonElement>>} */
const DeleteIconButton = React.forwardRef(function DeleteIconButton(
    { title = 'Delete', perm, allowed = true, disabled = false, size = 'small', fontSize = 'small', overImage = false, sx, tooltipProps, ...rest },
    ref,
) {
    const permitted = useDeletePermitted(perm, allowed);
    const button = (
        <IconButton
            ref={ref}
            aria-label={title || 'Delete'}
            {...rest}
            sx={overImage ? [OVER_IMAGE_SX, ...(Array.isArray(sx) ? sx : [sx])] : sx}
            color="error"
            size={size}
            disabled={disabled || !permitted}
        >
            <DeleteOutlinedIcon fontSize={fontSize} />
        </IconButton>
    );
    if (!title) {
        return button;
    }
    return (
        <Tooltip title={title} describeChild {...tooltipProps}>
            <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>{button}</span>
        </Tooltip>
    );
});

export default DeleteIconButton;
